import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { verifyMagicLink, getSessionId } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const MagicLinkVerify = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuthUser } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  const token = searchParams.get('token');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const verify = async () => {
      if (!token) {
        setStatus('error');
        setError('No verification token found');
        return;
      }

      try {
        // Get anonymous session to attach profile
        const anonymousSessionId = getSessionId();
        
        const result = await verifyMagicLink(token, anonymousSessionId);

        if (result.user && result.session_token) {
          setAuthUser(result.user, result.session_token);
          setStatus('success');
          
          // Check for redirect intent (e.g., user was trying to join Circle)
          const redirectIntent = localStorage.getItem('ohmnai_redirect_intent');
          localStorage.removeItem('ohmnai_redirect_intent');
          
          // Also check for auth redirect from EmailGate
          const authRedirect = localStorage.getItem('ohmnai_auth_redirect');
          localStorage.removeItem('ohmnai_auth_redirect');
          
          // Redirect after a short delay (prioritize redirect intent, then auth redirect)
          setTimeout(() => {
            navigate(redirectIntent || authRedirect || '/dashboard', { replace: true });
          }, 2000);
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        console.error('Magic link verification error:', err);
        setStatus('error');
        setError(err.message || 'Failed to verify magic link');
      }
    };

    verify();
  }, [token, navigate, setAuthUser]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern flex items-center justify-center">
      <div className="bg-fern-card rounded-2xl p-10 border border-calm-border shadow-lg max-w-md w-full mx-4 text-center">
        {status === 'verifying' && (
          <div className="space-y-6">
            <Loader2 className="h-16 w-16 animate-spin text-forest mx-auto" />
            <h1 className="text-2xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
              Verifying your link...
            </h1>
            <p className="text-calm-text-secondary">
              Please wait while we sign you in.
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6 fade-in-up">
            <div className="w-20 h-20 bg-herbal-light rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-12 w-12 text-forest" />
            </div>
            <h1 className="text-2xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
              You&apos;re signed in!
            </h1>
            <p className="text-calm-text-secondary">
              Redirecting you to your dashboard...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
            <h1 className="text-2xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
              Link expired or invalid
            </h1>
            <p className="text-calm-text-secondary">
              {error || 'This magic link may have expired or already been used.'}
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/auth/email')}
                className="bg-forest hover:bg-forest-dark text-white px-8 py-6 text-base rounded-xl"
              >
                Request a new link
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-calm-border text-forest hover:bg-fern px-8 py-6 text-base rounded-xl"
              >
                Back to Home
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicLinkVerify;
